// TypeScript mirror of src/Protocol/Messages.cs. The host serializes with camelCase
// names and writes absent optional values as null.

export const PROTOCOL_VERSION = '2';

export interface ActionPayload {
  kind: string;
  data?: Record<string, unknown> | null;
}

export interface ClientMessage {
  type: 'join' | 'snapshot' | 'command' | 'query' | 'presence' | 'ping' | 'save' | 'undo' | 'redo';
  protocolVersion: string;
  requestId?: string;
  seat?: string;
  seatToken?: string;
  expectedRevision?: number;
  decisionId?: string;
  action?: ActionPayload;
  query?: ActionPayload;
  hoverObjectId?: string | null;
}

export interface FieldSchema {
  name: string;
  type: 'string' | 'integer' | 'boolean' | 'array' | 'object';
  required?: boolean | null;
  minimum?: number | null;
  maximum?: number | null;
  maxLength?: number | null;
  minItems?: number | null;
  maxItems?: number | null;
  values?: string[] | null;
  items?: FieldSchema | null;
  fields?: FieldSchema[] | null;
}

export interface ActionSchema {
  kind: string;
  fields: FieldSchema[];
}

export interface PackageUiRef { api: 'ui-sdk-1'; url: string; sha256: string }

export interface PresentationRef {
  module?: PackageUiRef | null;
  id: string;
  version: string;
  manifestUrl: string;
  sha256: string;
}

/** One player-selectable table layout; sent only when a game has two or more (docs/ui-variants-design.md). */
export interface PresentationVariantRef {
  id: string;
  label: string;
  summary?: string | null;
  formFactor?: 'any' | 'phone' | 'desktop';
  presentation: PresentationRef;
}

export interface WireEvent {
  kind: string;
  message?: string | null;
  audience?: string | null;
}
export interface PublicProfile { displayName: string; primaryColor: string; secondaryColor: string; iconUrl?: string | null }
export interface PublicSeatProfile { seat: string; profile: PublicProfile }

export interface ServerMessage {
  profiles?: PublicSeatProfile[] | null;
  paused?: boolean;
  type: string;
  protocolVersion: string;
  requestId?: string | null;
  error?: string | null;
  accepted?: boolean | null;
  revision: number;
  decisionId?: string | null;
  matchId?: string | null;
  gameId?: string | null;
  playerId?: string | null;
  seat?: string | null;
  sessionId?: string | null;
  deviceId?: string | null;
  observation?: unknown;
  events?: WireEvent[] | null;
  hoverObjectId?: string | null;
  expiresAt?: string | null;
  description?: unknown;
  actionSchemas?: ActionSchema[] | null;
  title?: string | null;
  summary?: string | null;
  presentation?: PresentationRef | null;
  presentationVariants?: PresentationVariantRef[] | null;
  result?: unknown;
  /** Undo and history metadata on snapshots (docs/undo-and-replay-host-contract.md); absent from hosts without undo. */
  canUndo?: boolean | null;
  canRedo?: boolean | null;
  undoLabel?: string | null;
  historyLabel?: string | null;
  beforeRevision?: number | null;
  commandSeat?: string | null;
  operation?: 'command' | 'undo' | 'redo' | null;
}

/** Human-readable text for host error codes. Unknown codes are shown as-is. */
export const ERROR_TEXT: Record<string, string> = {
  stale_revision: 'The game moved on before your action arrived. Here is the current position.',
  closed_decision: 'That decision is already closed.',
  illegal_action: 'That move is not allowed right now.',
  invalid_action: 'That move is not allowed right now.',
  effect_failed: 'The game could not process that. Try another action.',
  invalid_capability: 'This seat link is not valid for this game.',
  seat_requires_capability: 'This seat needs its own join link.',
  seat_is_bot: 'A bot is playing this seat.',
  spectator_cannot_command: 'Spectators cannot make moves.',
  persistence_failed: 'The host could not save that move. Try again.',
  rate_limited: 'Too many requests. Wait a moment.',
  request_id_conflict: 'That request was already used for a different action.',
  undo_unavailable: 'There is no move to undo here.',
  redo_unavailable: 'There is no move to redo.',
};

export function errorText(code: string | null | undefined): string {
  if (!code) return 'Something went wrong.';
  return ERROR_TEXT[code] ?? `The host rejected that action (${code}).`;
}
